import type { Row } from '@tanstack/react-table';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@src/design-system/button';
import { Edit2, Eye } from 'iconsax-react';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from '@src/design-system/dialog';
import { entities } from '@shared/constants/entities';
import { HasPermission } from '@features/roles/guards/has-permission';
import type { TUniversity } from '../types';
import CreateUniversityModal from '../components/create-university-modal';
import { SingleDeleteAction } from '@src/design-system/molecules/ticlick-table/actions';
import universitiesApi from '../services/universities-api';

interface RowActionsCellProps {
  row: Row<TUniversity>;
}

const RowActionsCell = ({ row }: RowActionsCellProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { mutateAsync: deleteUniversity, isPending } =
    universitiesApi.useDelete();

  const handleViewClick = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('view', String(row.original.id));
    setSearchParams(newSearchParams);
  };

  return (
    <div className='flex items-center justify-center'>
      <HasPermission
        entity={entities.university}
        action='change'
      >
        <Button
          variant='ghost'
          size={'iconSm'}
          className='pt-0.5'
        >
          <Dialog>
            <DialogTrigger className='cursor-pointer'>
              <Edit2
                size='16'
                className='text-gray-800'
                variant='TwoTone'
              />
            </DialogTrigger>
            <DialogContent className='max-w-[600px]'>
              <CreateUniversityModal university={row.original} />
            </DialogContent>
          </Dialog>
        </Button>
      </HasPermission>

      <HasPermission
        entity={entities.university}
        action='delete'
      >
        <SingleDeleteAction
          id={row.original.id}
          label='university'
          isLoading={isPending}
          onConfirm={(closeModal) =>
            deleteUniversity({ id: row.original.id }, { onSuccess: closeModal })
          }
        />
      </HasPermission>

      <HasPermission
        entity={entities.university}
        action='view'
      >
        <Button
          variant='ghost'
          size={'iconSm'}
          onClick={handleViewClick}
        >
          <Eye
            size='20'
            cursor='pointer'
            variant='TwoTone'
            color='var(--color-gray-800)'
          />
        </Button>
      </HasPermission>
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default RowActionsCell;
